import { Loader2 } from "lucide-react";
import { IconProgressCircle } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import type { QuestionStatus } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";

type Props = {
  questionStatus: QuestionStatus | undefined;
  className?: string;
  useSkeletonLoader?: boolean;
};

export const QuestionStatusCard = ({
  questionStatus,
  className,
  useSkeletonLoader = false,
}: Props) => {
  const t = useI18n();

  const getStatusText = () => {
    switch (questionStatus) {
      case "Unfamiliar":
        return t("quiz.question.status.unfamiliar");
      case "Still Learning":
        return t("quiz.question.status.stillLearning");
      case "Familiar":
        return t("quiz.question.status.familiar");
      case "Mastered":
        return t("quiz.question.status.mastered");
      default:
        return "";
    }
  };

  return (
    <div
      className={cn(
        "flex flex-row justify-center items-center gap-2 rounded-lg py-2 px-3",
        !questionStatus
          ? "bg-gray-100" // Lighter background for loading state
          : questionStatus === "Unfamiliar"
            ? "bg-[#EF00001A]"
            : questionStatus === "Still Learning"
              ? "bg-[#FF80001A]"
              : questionStatus === "Familiar"
                ? "bg-[#0077FF1A]"
                : "bg-[#1DAD2C1A]",
        className,
      )}
    >
      {questionStatus ? (
        <IconProgressCircle
          strokeWidth={4}
          fill={
            questionStatus === "Unfamiliar"
              ? "#EF0000"
              : questionStatus === "Still Learning"
                ? "#FF8000"
                : questionStatus === "Familiar"
                  ? "#0077FF"
                  : "#1DAD2C"
          }
          className="size-4"
        />
      ) : useSkeletonLoader ? (
        <div className="size-4 rounded-full bg-gray-200 animate-pulse" />
      ) : (
        <Loader2 className="animate-spin text-gray-400 size-4" /> // More subtle color for spinner
      )}
      <p
        className={cn(
          "font-normal text-xs",
          !questionStatus
            ? "text-gray-400" // More subtle color for loading text
            : questionStatus === "Unfamiliar"
              ? "text-[#EF0000]"
              : questionStatus === "Still Learning"
                ? "text-[#FF8000]"
                : questionStatus === "Familiar"
                  ? "text-[#0077FF]"
                  : "text-[#1DAD2C]",
        )}
      >
        {questionStatus ? (
          getStatusText()
        ) : useSkeletonLoader ? (
          <Skeleton className="w-16 h-4 bg-gray-200 rounded animate-pulse" />
        ) : (
          "Loading..."
        )}
      </p>
    </div>
  );
};
